import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import Modal from "@/components/Modal";
import { useInquiry } from "@/context/InquiryContext";
import useProducts from "@/hooks/useProducts";
import { submitOrder } from "@/lib/api";
import { btnPrimary, inputCls, labelCls } from "@/lib/site";

export default function OrderModal() {
  const { order, closeOrder } = useInquiry();
  const { data: products } = useProducts();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (order.open)
      setForm({
        product_name: order.product || "",
        quantity: 1,
        name: "",
        company: "",
        email: "",
        phone: "",
        delivery_location: "",
        notes: "",
      });
  }, [order.open, order.product]);

  const options = useMemo(() => {
    const names = (products || []).map((p) => p.name);
    if (form?.product_name && !names.includes(form.product_name)) return [form.product_name, ...names];
    return names;
  }, [products, form?.product_name]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await submitOrder(form);
      toast.success(`Order request received — Ref ${res.ref}`, {
        description: "Our sales team will contact you shortly. A confirmation email is on its way.",
      });
      closeOrder();
    } catch {
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={order.open}
      onClose={closeOrder}
      testId="order-modal"
      title="Place an Order"
      subtitle="No payment needed — our team confirms stock, specs and delivery first."
    >
      {form && (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Product *</label>
            <select
              required
              data-testid="order-modal-product-select"
              className={inputCls}
              value={form.product_name}
              onChange={set("product_name")}
            >
              <option value="" disabled>
                Select a product
              </option>
              {options.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Quantity *</label>
              <input
                type="number"
                min="1"
                required
                data-testid="order-modal-quantity-input"
                className={inputCls}
                value={form.quantity}
                onChange={set("quantity")}
              />
            </div>
            <div>
              <label className={labelCls}>Phone *</label>
              <input required data-testid="order-modal-phone-input" className={inputCls} placeholder="+91…" value={form.phone} onChange={set("phone")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Your Name *</label>
              <input required data-testid="order-modal-name-input" className={inputCls} value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label className={labelCls}>Company</label>
              <input data-testid="order-modal-company-input" className={inputCls} value={form.company} onChange={set("company")} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Email *</label>
            <input type="email" required data-testid="order-modal-email-input" className={inputCls} value={form.email} onChange={set("email")} />
          </div>
          <div>
            <label className={labelCls}>Delivery Location *</label>
            <input required data-testid="order-modal-location-input" className={inputCls} placeholder="City, State" value={form.delivery_location} onChange={set("delivery_location")} />
          </div>
          <div>
            <label className={labelCls}>Notes / Specifications</label>
            <textarea rows={3} data-testid="order-modal-notes-input" className={inputCls} placeholder="Chamber size, voltage, accessories…" value={form.notes} onChange={set("notes")} />
          </div>
          <button data-testid="order-modal-submit-btn" className={`${btnPrimary} w-full`} disabled={busy}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {busy ? "Submitting…" : "Submit Order Request"}
          </button>
          <p className="text-center text-[11px] text-slate-400">
            You'll receive a confirmation email with your reference number.
          </p>
        </form>
      )}
    </Modal>
  );
}
